import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  Ban,
  BadgeCheck,
  Clock,
  FileEdit,
  LucideIcon,
  MessageCircleQuestion,
  Search,
  ShieldAlert,
  XCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  CertificationStatus,
  getStatusLabelKey,
} from "../../../../models/certificationStatus";
import React from "react";

export interface StatusVisual {
  icon: LucideIcon;
  /** Classes for the pill badge (bg + text). */
  badgeClass: string;
  /** Left accent border colour used on cards. */
  accentBorder: string;
  /** Soft background used for decision blocks. */
  softBg: string;
}

const STATUS_VISUALS: Record<CertificationStatus, StatusVisual> = {
  PENDING: {
    icon: Clock,
    badgeClass:
      "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    accentBorder: "border-l-slate-400",
    softBg: "bg-slate-50 dark:bg-slate-900/40",
  },
  IN_REVIEW: {
    icon: Search,
    badgeClass:
      "bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300",
    accentBorder: "border-l-indigo-400",
    softBg: "bg-indigo-50 dark:bg-indigo-950/40",
  },
  WAITING_FOR_USER: {
    icon: MessageCircleQuestion,
    badgeClass:
      "bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300",
    accentBorder: "border-l-orange-400",
    softBg: "bg-orange-50 dark:bg-orange-950/40",
  },
  CERTIFIED: {
    icon: BadgeCheck,
    badgeClass:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
    accentBorder: "border-l-emerald-500",
    softBg: "bg-emerald-50 dark:bg-emerald-950/40",
  },
  CERTIFIED_WITH_RESERVATIONS: {
    icon: ShieldAlert,
    badgeClass:
      "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
    accentBorder: "border-l-amber-500",
    softBg: "bg-amber-50 dark:bg-amber-950/40",
  },
  CORRECTED: {
    icon: FileEdit,
    badgeClass: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
    accentBorder: "border-l-blue-500",
    softBg: "bg-blue-50 dark:bg-blue-950/40",
  },
  DECLINED: {
    icon: XCircle,
    badgeClass: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300",
    accentBorder: "border-l-red-500",
    softBg: "bg-red-50 dark:bg-red-950/40",
  },
  CANCELLED: {
    icon: Ban,
    badgeClass: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
    accentBorder: "border-l-gray-400",
    softBg: "bg-gray-50 dark:bg-gray-900/40",
  },
};

export function getStatusVisual(status: CertificationStatus): StatusVisual {
  return STATUS_VISUALS[status] ?? STATUS_VISUALS.PENDING;
}

export interface StatusBadgeProps {
  status: CertificationStatus;
  className?: string;
}

/** Pill badge with a status-specific icon, colour and translated label. */
export function StatusBadge({ status, className }: StatusBadgeProps) {
  const { t } = useTranslation();
  const visual = getStatusVisual(status);
  const Icon = visual.icon;
  return (
    <Badge
      variant="outline"
      className={cn(
        "gap-1.5 border-transparent px-2.5 py-1 font-medium",
        visual.badgeClass,
        className
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {t(getStatusLabelKey(status))}
    </Badge>
  );
}
